// src/pages/PatientLabTestsPage.jsx
// Route: /patient/lab-tests
// Shows the patient's lab test orders.
// Sprint 14 (29 Jun 2026): added appointmentDate/appointmentTime display from child tables.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import PageHead from '../components/PageHead';
import AppointmentsTabs from '../components/AppointmentsTabs';
import EmptyState from '../components/EmptyState';
import { usePatient } from '../context/PatientContext';
import api from '../services/api';
import Icon from '../components/Icon';
import { PANEL_TIER_DISPLAY_NAMES } from '../utils/labTestReminders';

const STATUS_COLORS = {
  ORDERED:          { bg: 'var(--amber-bg)', color: 'var(--amber)' },
  CONFIRMED:        { bg: 'var(--green-bg)', color: 'var(--green)' },
  PENDING:          { bg: 'var(--amber-bg)', color: 'var(--amber)' },
  COMPLETED:        { bg: 'var(--blue-bg)',  color: 'var(--blue)'  },
  CANCELLED:        { bg: 'var(--red-bg)',   color: 'var(--red)'   },
  FAILED:           { bg: 'var(--red-bg)',   color: 'var(--red)'   },
  SAMPLE_COLLECTED: { bg: 'var(--green-bg)', color: 'var(--green)' },
  REFUND_REQUESTED: { bg: 'var(--amber-bg)', color: 'var(--amber)' },
  REFUNDED:         { bg: 'var(--bg-subtle)', color: 'var(--text-secondary)' },
};

// Status labels shown to the patient — avoids leaking internal enum values
const STATUS_LABELS = {
  ORDERED:          'Booked',
  CONFIRMED:        'Confirmed',
  PENDING:          'Pending',
  COMPLETED:        'Results Ready',
  CANCELLED:        'Cancelled',
  FAILED:           'Failed',
  SAMPLE_COLLECTED: 'Sample Collected',
  REFUND_REQUESTED: 'Refund Requested',
  REFUNDED:         'Refunded',
};

function statusStyle(status) {
  return STATUS_COLORS[status?.toUpperCase()] ?? { bg: 'var(--bg-subtle)', color: 'var(--text-secondary)' };
}

function statusLabel(status) {
  return STATUS_LABELS[status?.toUpperCase()] ?? (status ?? 'Unknown');
}

/**
 * Formats an ISO date string (YYYY-MM-DD) to "Mon, 30 Jun"
 */
function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * Formats a LocalTime string (HH:mm:ss or HH:mm) to "09:00 AM"
 */
function formatTime(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

// 15 Jul 2026: display names matching PanelDefinitionService's registry
// exactly (T1_PANEL.name / T2_PANEL.name / etc in the backend) — used to
// show a consistent Previu panel name even for historical orders whose
// stored `plan` text still holds the raw pre-fix Thyrocare SKU name (e.g.
// "AAROGYAM 1.1"). panelTier was already backfilled for existing orders via
// migration V54, so no new data migration is needed here — this is purely
// a display-time lookup, non-destructive to the underlying stored data.
//
// 16 Sep 2026: ONCOVERYX retired (see backend V130 migration) and is kept
// here ONLY so a patient's past ONCOVERYX order still shows a readable name
// instead of falling back to a raw SKU string — it is no longer offered for
// new orders anywhere. TUMOUR_MALE/TUMOUR_FEMALE added as its replacement.

const ACTIVE_STATUSES = ['ORDERED', 'CONFIRMED', 'PENDING', 'SAMPLE_COLLECTED', 'REFUND_REQUESTED'];
const isActive = o => ACTIVE_STATUSES.includes(o.status?.toUpperCase());

const panelNameOf = order => PANEL_TIER_DISPLAY_NAMES[order.panelTier]
  || order.plan || order.testCode || order.id?.slice(0, 8) || 'Order';

// Collection slot as a Date, or null when the order has none yet.
function collectionStart(o) {
  if (!o.appointmentDate) return null;
  return new Date(`${o.appointmentDate}T${o.appointmentTime || '00:00'}`);
}

function OrderCard({ order, onOpen }) {
  const s = statusStyle(order.status);
  const label = statusLabel(order.status);
  const apptDate = formatDate(order.appointmentDate);
  const apptTime = formatTime(order.appointmentTime);
  const orderedOn = order.createdAt
    ? new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const panelName = panelNameOf(order);

  return (
    <div className="lab-order-card">
      <div className="lab-order-card__top">
        <div className="lab-order-card__icon" aria-hidden="true"><Icon name="document" size={20} /></div>
        <span className="lab-status" style={{ color: s.color }}>{label}</span>
      </div>
      <div>
        <div className="lab-order-card__name">{panelName}</div>
        <div className="lab-order-card__meta">
          {apptDate ? (
            <span><Icon name="calendar" size={14} /> {apptTime ? `${apptDate} · ${apptTime}` : apptDate}</span>
          ) : orderedOn ? (
            <span><Icon name="calendar" size={14} /> Ordered {orderedOn}</span>
          ) : null}
          {order.partnerOrderId && <span>Reference {order.partnerOrderId}</span>}
        </div>
      </div>
      {(order.noOfTests != null || order.price != null) && (
        <div className="lab-order-card__foot">
          <span>{order.noOfTests != null ? `${order.noOfTests} tests` : ''}</span>
          {order.price != null && (
            <span className="lab-order-card__price">₹{Number(order.price).toLocaleString('en-IN')}</span>
          )}
        </div>
      )}
      <button type="button" className="lab-order-card__view" aria-label={`View order — ${panelName}`}
        onClick={onOpen}>
        View order <Icon name="arrowRight" size={14} />
      </button>
    </div>
  );
}

export default function PatientLabTestsPage() {
  const { patient } = usePatient();
  const navigate    = useNavigate();
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    api.get(`/lab-test-orders?patientId=${patient.id}&page=0&size=20`)
      .then(res => {
        if (!cancelled) {
          setOrders(res.data?.items ?? res.data?.content ?? res.data?.orders ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load lab test orders. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [patient?.id]);

  const openOrder = id => navigate(`/patient/lab-tests/${id}`);
  const active = orders.filter(isActive);
  const past   = orders.filter(o => !isActive(o));
  const ready  = orders
    .filter(o => o.status?.toUpperCase() === 'COMPLETED')
    .sort((x, y) => new Date(y.createdAt || 0) - new Date(x.createdAt || 0));
  const now = new Date();
  const nextCollection = active.map(collectionStart).filter(d => d && d >= now).sort((x, y) => x - y)[0];

  return (
    <PatientAppShell breadcrumbs={[{ label: 'My Appointments', to: '/patient/appointments' }, { label: 'Lab Tests' }]}>
      <div className="page-shell appt-page--wide">
        <AppointmentsTabs />
        <PageHead
          eyebrow="Your health journey"
          title="Lab tests"
          subtitle="Track your home collection orders from booking to results."
          actions={(
            <button className="btn btn-accent" style={{ borderRadius: 999 }}
              onClick={() => navigate('/patient/lab-tests/order')}>
              + Order lab test
            </button>
          )}
        />

        {loading && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
            Loading orders…
          </div>
        )}

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        {!loading && !error && orders.length === 0 && (
          <EmptyState
            framed
            icon="vial"
            title="No lab tests booked yet"
            message="Order a panel and a phlebotomist comes to you. Results land here
                     automatically and feed straight into your health story."
            action={{ label: 'Order a lab test', onClick: () => navigate('/patient/lab-tests/order') }}
            secondaryAction={{ label: 'Upload an existing report', onClick: () => navigate('/patient/upload') }}
            hint="Home collection across India, usually within 48 hours."
          />
        )}

        {!loading && !error && orders.length > 0 && (
          <>
            <div className="lab-summary">
              <div className="lab-summary__item">
                <div className="lab-summary__label">Active orders</div>
                <div className="lab-summary__value">{active.length}</div>
              </div>
              <div className="lab-summary__item">
                <div className="lab-summary__label">Next collection</div>
                <div className="lab-summary__value">
                  {nextCollection
                    ? `${nextCollection.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, ${formatTime(
                        `${nextCollection.getHours()}:${nextCollection.getMinutes()}`)}`
                    : '—'}
                </div>
              </div>
              <div className="lab-summary__item">
                <div className="lab-summary__label">Results available</div>
                <div className="lab-summary__value">
                  {ready.length} {ready.length === 1 ? 'report' : 'reports'}
                </div>
              </div>
            </div>

            {active.length > 0 && (
              <section style={{ marginBottom: 12 }}>
                <div className="appt-section-head">
                  <h2 className="appt-section-title">
                    Active orders <span className="appt-count">{active.length}</span>
                  </h2>
                  <span className="appt-section-hint">Home collection</span>
                </div>
                <div className="lab-order-grid">
                  {active.map(o => <OrderCard key={o.id} order={o} onOpen={() => openOrder(o.id)} />)}
                </div>
              </section>
            )}

            {ready.length > 0 && (
              <div className="lab-results-banner">
                <span className="lab-results-banner__icon" aria-hidden="true"><Icon name="download" size={20} /></span>
                <div className="lab-results-banner__text">
                  <div className="lab-results-banner__title">Your latest lab results are ready</div>
                  <div className="lab-results-banner__sub">
                    {panelNameOf(ready[0])}
                    {ready[0].createdAt && ` · ${new Date(ready[0].createdAt)
                      .toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                  </div>
                </div>
                <button type="button" className="btn btn-ghost" style={{ borderRadius: 999 }}
                  onClick={() => openOrder(ready[0].id)}>
                  <Icon name="download" size={14} /> View results
                </button>
              </div>
            )}

            {past.length > 0 && (
              <section style={{ marginTop: 36 }}>
                <div className="appt-section-head">
                  <h2 className="appt-section-title appt-section-title--display">
                    Past orders <span className="appt-count appt-count--muted">{past.length}</span>
                  </h2>
                </div>
                <div className="lab-order-grid">
                  {past.map(o => <OrderCard key={o.id} order={o} onOpen={() => openOrder(o.id)} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </PatientAppShell>
  );
}
